import { action, computed, observable, makeAutoObservable } from 'mobx';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { UploadImageState } from 'src/AppState/UploadImageState/UploadImageState';
import * as t from 'io-ts';
import { CasinoBannersResponseType, CasinoBannerType, CollectionType } from 'src/domains/casino/models/models';
import { openapiBannersStaffGetBannersRequest } from 'src/domains/casino/api/generated/banners/openapi_banners_staff_get_banners';
import { Session } from 'src_common/sdk/session';
import { openapiBannersStaffEditBannerRequest } from 'src/domains/casino/api/generated/banners/openapi_banners_staff_edit_banner';
import { openapiBannersStaffCreateBannerRequest } from 'src/domains/casino/api/generated/banners/openapi_banners_staff_create_banner';
import { openapiBannersStaffDeleteBannerRequest } from 'src/domains/casino/api/generated/banners/openapi_banners_staff_delete_banner';
import { openapiBannersStaffChangeBannersOrderRequest } from 'src/domains/casino/api/generated/banners/openapi_banners_staff_change_banners_order';

const SelectionsDetailsIO = t.interface({
    id: t.number,
    label: t.string,
});

export enum RedirectTypeEnum {
    REDIRECT_URL = 'REDIRECT_URL',
    REDIRECT_GAME = 'REDIRECT_GAME',
}

export type SelectionsDetailsType = t.TypeOf<typeof SelectionsDetailsIO>;

type FormType = {
    date_end: string;
    date_start: string;
    published: boolean;
    redirect_game: null | string;
    redirect_type: string;
    redirect_url: string;
    terms_full: string;
    terms_headline: string;
    title: string;
};

interface RedirectOptionsType {
    key: string;
    name: string;
}

type DirectionType = 'up' | 'down';

export class CasinoBannersDataState {
    @observable private readonly casinoBannersResource: Resource<CasinoBannersResponseType>;
    @observable public isEditMode: boolean = false;
    @observable private singleBanner: CasinoBannerType | null = null;
    @observable public errorMessageForList: string | null = null;
    @observable public redirectionError: string | null = null;
    @observable public imagesError: string | null = null;

    public isPublished: FormInputState<boolean, boolean>;
    public title: FormInputState<string, string>;
    public dateStart: FormInputState<string, string>;
    public dateEnd: FormInputState<string, string>;
    public redirectType: FormInputState<string, string>;
    public redirectGame: FormInputState<string, string>;
    public redirectUrl: FormInputState<string, string>;
    public termsHeadline: FormInputState<string, string>;
    public termsFull: FormInputState<string, string>;

    public imageDesktop: UploadImageState;
    public imageTablet: UploadImageState;
    public imageMobile: UploadImageState;

    private readonly form: FormModel<FormType>;

    private readonly noEmptyValidationMessage = 'This field is required.';

    public constructor(
        public collection: CollectionType,
        private readonly session: Session
    ) {
        this.casinoBannersResource = new Resource(async (): Promise<CasinoBannersResponseType> => {
            const resource = this.session.call(openapiBannersStaffGetBannersRequest, { collection_slug: collection });
            return resource;
        });

        this.title = FormInputState.new('').map(validateNotEmpty(this.noEmptyValidationMessage));
        this.dateStart = FormInputState.new('').map(validateNotEmpty(this.noEmptyValidationMessage));
        this.dateEnd = FormInputState.new('').map(validateNotEmpty(this.noEmptyValidationMessage));
        this.redirectType = FormInputState.new('');
        this.redirectGame = FormInputState.new('');
        this.redirectUrl = FormInputState.new('');
        this.termsHeadline = FormInputState.new('');
        this.termsFull = FormInputState.new('');
        this.isPublished = FormInputState.new<boolean>(false);

        this.form = FormModel.group({
            published: this.isPublished,
            title: this.title,
            date_start: this.dateStart,
            date_end: this.dateEnd,
            redirect_type: this.redirectType,
            redirect_url: this.redirectUrl,
            redirect_game: this.redirectGame,
            terms_headline: this.termsHeadline,
            terms_full: this.termsFull,
        });

        this.imageDesktop = new UploadImageState('casino_banner/desktop_image');
        this.imageTablet = new UploadImageState('casino_banner/tablet_image');
        this.imageMobile = new UploadImageState('casino_banner/mobile_image');
        makeAutoObservable(this);
    }

    // GET BANNERS
    @computed public get casinoBanners(): Array<CasinoBannerType> {
        const data = this.casinoBannersResource.get();
        if (data.type === 'ready') {
            return data.value.banners;
        }
        return [];
    }

    // CREATE BANNER

    @action public handleCreate = async (): Promise<void> => {
        this.form.setAsVisited();
        const { result } = this.form;

        if (result.value.type === 'ok') {
            if (this.isRedirectionValid === false) {
                this.redirectionError = 'Redirection option is invalid.';
                await this.wait(3000);
                this.redirectionError = null;
                return;
            }

            if (this.areImagesValid === false) {
                this.imagesError = 'All 3 images are required.';
                await this.wait(3000);
                this.imagesError = null;
                return;
            }

            if (result.value.data.redirect_type === RedirectTypeEnum.REDIRECT_GAME) {
                result.value.data.redirect_url = '';
            }

            if (result.value.data.redirect_type === RedirectTypeEnum.REDIRECT_URL) {
                result.value.data.redirect_game = null;
            }

            const params = {
                banner_details: {
                    ...result.value.data,
                    redirect_game:
                        result.value.data.redirect_game === null ? null : parseInt(result.value.data.redirect_game, 10),
                    image_desktop: this.imageDesktop.imgUrl,
                    image_tablet: this.imageTablet.imgUrl,
                    image_mobile: this.imageMobile.imgUrl,
                },
                collection: this.collection,
            };

            await this.session.call(openapiBannersStaffCreateBannerRequest, { requestBody: params });
            await this.casinoBannersResource.refresh();
            this.resetForm();
        }
    };

    // EDIT BANNER

    @action public setEditBanner = async (banner: CasinoBannerType): Promise<void> => {
        this.singleBanner = banner;

        // TODO: check all type
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        this.title.setValue(banner.title ?? '');
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        this.dateStart.setValue(banner.date_start ?? '');
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        this.dateEnd.setValue(banner.date_end ?? '');
        this.redirectType.setValue(banner.redirect_type ?? '');
        this.redirectUrl.setValue(banner.redirect_url ?? '');
        this.redirectGame.setValue(banner.redirect_game?.toString() ?? '');
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        this.termsHeadline.setValue(banner.terms_headline ?? '');
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        this.termsFull.setValue(banner.terms_full ?? '');
        this.isPublished.setValue(banner.published);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        this.imageDesktop.handleChooseImage(banner.image_desktop ?? '');
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        this.imageTablet.handleChooseImage(banner.image_tablet ?? '');
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        this.imageMobile.handleChooseImage(banner.image_mobile ?? '');
    };

    @action public handleEdit = async (): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;

        if (result.value.type === 'ok' && this.singleBanner !== null) {
            if (this.isRedirectionValid === false) {
                this.redirectionError = 'Redirection option is invalid.';
                await this.wait(3000);
                this.redirectionError = null;
                return;
            }

            if (this.areImagesValid === false) {
                this.imagesError = 'All 3 images are required.';
                await this.wait(3000);
                this.imagesError = null;
                return;
            }

            if (result.value.data.redirect_type === RedirectTypeEnum.REDIRECT_GAME) {
                result.value.data.redirect_url = '';
            }

            if (result.value.data.redirect_type === RedirectTypeEnum.REDIRECT_URL) {
                result.value.data.redirect_game = null;
            }

            const banner_details = {
                ...result.value.data,
                redirect_game:
                    result.value.data.redirect_game === null ? null : parseInt(result.value.data.redirect_game, 10),
                image_desktop: this.imageDesktop.imgUrl,
                image_tablet: this.imageTablet.imgUrl,
                image_mobile: this.imageMobile.imgUrl,
            };

            const params = {
                banner_id: this.singleBanner.id,
                banner_details,
                collection: this.collection,
            };

            await this.session.call(openapiBannersStaffEditBannerRequest, { requestBody: params });
            await this.casinoBannersResource.refresh();
            this.resetForm();
        }
    };

    @action public setBannerPublished = async (bannerId: number): Promise<void> => {
        const banner = this.casinoBanners.find((banner) => banner.id === bannerId);
        if (banner === undefined) {
            console.error('Cannot find the banner with provided ID.');
            return;
        }
        const params = {
            banner_id: bannerId,
            banner_details: {
                ...banner,
                published: !banner.published,
            },
            collection: this.collection,
        };
        await this.session.call(openapiBannersStaffEditBannerRequest, { requestBody: params });
        await this.casinoBannersResource.refresh();
        this.resetForm();
    };

    @action public setEditMode = (value: boolean): void => {
        this.isEditMode = value;
    };

    // DELETE BANNER

    @action public deleteBanner = async (bannerId: number): Promise<void> => {
        const banner = this.casinoBanners.find((banner) => banner.id === bannerId);
        if (banner === undefined) {
            console.error('Cannot find the banner with provided ID.');
            return;
        }
        const params = {
            banner_id: bannerId,
            collection: this.collection,
        };
        await this.session.call(openapiBannersStaffDeleteBannerRequest, { requestBody: params });
        await this.casinoBannersResource.refresh();
        this.resetForm();
    };

    // SORT BANNERS

    public moveBanner = async (bannerId: number, direction: DirectionType): Promise<void> => {
        const movedBannerIndex = this.casinoBanners.findIndex((banner) => banner.id === bannerId);
        const movedBanner = this.casinoBanners[movedBannerIndex];
        const sortedBanners = [...this.casinoBanners];
        const errorMessage = `Cannot move item ${direction}.`;
        if (direction === 'up') {
            if (movedBannerIndex > 0 && movedBanner !== undefined) {
                sortedBanners.splice(movedBannerIndex - 1, 0, movedBanner);
                sortedBanners.splice(movedBannerIndex + 1, 1);
            } else {
                return this.handleError(errorMessage);
            }
        }
        if (direction === 'down') {
            if (movedBannerIndex < this.casinoBanners.length - 1 && movedBanner !== undefined) {
                sortedBanners.splice(movedBannerIndex + 2, 0, movedBanner);
                sortedBanners.splice(movedBannerIndex, 1);
            } else {
                return this.handleError(errorMessage);
            }
        }
        const sortedBannersIds = sortedBanners.map((banner) => banner.id);
        const params = {
            collection: this.collection,
            sorted_banners_ids: sortedBannersIds,
        };
        await this.session.call(openapiBannersStaffChangeBannersOrderRequest, { requestBody: params });
        await this.casinoBannersResource.refresh();
        this.resetForm();
    };

    // HELPERS

    @computed public get isLoading(): boolean {
        const data = this.casinoBannersResource.get();
        return data.type === 'loading' ? true : false;
    }

    @computed public get isRedirectionValid(): boolean {
        const result = this.form.result;
        if (result.value.type === 'ok') {
            const { redirect_type, redirect_url, redirect_game } = result.value.data;
            if (redirect_type === RedirectTypeEnum.REDIRECT_URL) {
                return redirect_url !== '';
            }
            if (redirect_type === RedirectTypeEnum.REDIRECT_GAME) {
                return redirect_game !== null;
            }
        }
        return false;
    }

    @computed public get areImagesValid(): boolean {
        const result = this.form.result;
        if (result.value.type === 'ok') {
            const { imageDesktop, imageTablet, imageMobile } = this;
            if (imageDesktop.imgUrl === '' || imageTablet.imgUrl === '' || imageMobile.imgUrl === '') {
                return false;
            }
        }
        return true;
    }

    public get redirectOptions(): Array<RedirectOptionsType> {
        const redirectionList = [
            {
                key: RedirectTypeEnum.REDIRECT_URL,
                name: 'Direct link',
            },
            {
                key: RedirectTypeEnum.REDIRECT_GAME,
                name: 'Casino game',
            },
        ];
        return redirectionList;
    }

    @action public resetForm = (): void => {
        this.form.reset();
        this.isEditMode = false;
        this.singleBanner = null;
        this.imageDesktop.handleResetImage();
        this.imageTablet.handleResetImage();
        this.imageMobile.handleResetImage();
    };

    @action public handleResetForm = async (): Promise<void> => {
        if (this.isEditMode && this.singleBanner !== null) {
            await this.setEditBanner(this.singleBanner);
        } else {
            this.resetForm();
        }
    };

    @computed public get pageName(): string {
        if (this.collection === 'casino') {
            return 'Casino';
        }
        if (this.collection === 'live-casino') {
            return 'Live Casino';
        }
        return '';
    }

    public wait = async (time: number): Promise<void> => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, time);
        });
    };

    @action public handleError = async (message: string): Promise<void> => {
        this.errorMessageForList = message;
        await this.wait(3000);
        this.errorMessageForList = null;
    };

    @action public changeCollection = (collection: CollectionType): void => {
        this.collection = collection;
    };

    // --- DATE HANDLERS

    @computed private get parsedStartDate(): number | null {
        return Date.parse(this.dateStart.value);
    }

    @computed private get parsedEndDate(): number | null {
        return Date.parse(this.dateEnd.value);
    }

    @computed public get startDate(): Date | null {
        return this.parsedStartDate === null || isNaN(this.parsedStartDate) ? null : new Date(this.parsedStartDate);
    }

    @computed public get endDate(): Date | null {
        return this.parsedEndDate === null || isNaN(this.parsedEndDate) ? null : new Date(this.parsedEndDate);
    }
}
